<template>
  <div class="league-ranking-view">
    <!-- Loading Overlay -->
    <LoadingOverlay :loading="isLoading" :message="loadingMessage" />

    <!-- Skeleton Loader for initial load -->
    <SkeletonLoader v-if="isInitialLoading" type="table" />

    <!-- Share Dialog -->
    <v-dialog v-model="showShareDialog" max-width="600px">
      <LeagueShare
        :partnerId="partnerId"
        :tier="selectedTier"
        :partnerName="partnerName"
        :primaryColor="primaryColor"
        @close="showShareDialog = false"
        @copied="onLinkCopied"
      />
    </v-dialog>

    <!-- Main Content (hidden during initial load) -->
    <div v-show="!isInitialLoading">
      <!-- General Empty State - No league data at all -->
      <div v-if="hasAnyLeagueData === false" class="general-empty-state">
        <div class="page-header">
          <div class="header-content">
            <h1 class="text-h1 font-weight-bold page-title">
              {{ partnerName }} {{ $t('broker.app.navigation.leagues') || 'Leagues' }}
            </h1>
            <p class="text-body-1 text--darken-2 page-subtitle">{{ $t('broker.leagues.subtitle') || 'Set up and control your competition parameters.' }}</p>
          </div>
        </div>

        <div class="empty-state-container">
          <span class="empty-state-icon"></span>
          <h3>{{ $t('broker.leagues.noLeaguesAvailable') || 'No Leagues Available' }}</h3>
          <p>{{ $t('broker.leagues.noLeaguesAvailableDesc') || 'There are no active leagues for your partner at the moment. Leagues will appear here once they are configured.' }}</p>
        </div>
      </div>

      <!-- League UI - Has data -->
      <div v-if="hasAnyLeagueData === true">
        <div class="page-header">
          <div class="header-content">
            <div class="title-wrapper">
              <h1 class="text-h1 font-weight-bold page-title">
                {{ partnerName }} {{ $t('broker.app.navigation.leagues') || 'Leagues' }}
              </h1>
              <span class="tier-badge" :class="`tier-${selectedTier}`">
                <img :src="currentTierIcon" :alt="currentTierLabel" class="tier-badge-icon" />
                <div class="tier-badge-label">{{ currentTierLabel }}</div>
              </span>
            </div>
            <p class="text-body-1 text--darken-2 page-subtitle">{{ $t('broker.leagues.subtitle') || 'Set up and control your competition parameters.' }}</p>
          </div>
        </div>

        <div class="content-section">
          <LeagueTierTabs v-model="selectedTier" :primary-color="primaryColor" :tier-accent-color="tierAccentColor" />

          <div class="search-share-bar">
            <div class="search-container">
              <v-text-field
                v-model="searchQuery"
                :placeholder="$t('broker.leagues.search') || 'Search'"
                prepend-inner-icon="fa-regular fa-search"
                dense
                outlined
                hide-details
                class="search-input"
              />
            </div>
            <v-btn color="primary" class="share-btn" @click="openShareDialog">
              <i class="fa-regular fa-share-nodes mr-2"></i>
              {{ $t('broker.leagues.share') || 'Share' }}
            </v-btn>
          </div>

          <transition name="tier-fade" mode="out-in">
            <div :key="selectedTier" class="tier-content">
              <!-- Podium always shows top 3 from API (not affected by search) -->
              <div v-if="!isLoading && top3Communities.length > 0" class="podium-section">
                <LeaguePodiumCard
                  v-for="community in podiumOrder"
                  :key="community.id"
                  :community="community"
                  :place="community.rank"
                />
              </div>

              <!-- Table shows filtered results -->
              <div v-if="!isLoading && filteredCommunities.length > 0" class="table-section">
                <LeagueTable :communities="filteredCommunities" :primary-color="primaryColor" />
              </div>

              <!-- Empty state only shows when tier has no data at all (not when search returns empty) -->
              <div v-if="!isLoading && communities.length === 0" class="empty-state">
                <span class="empty-state-icon"></span>
                <h3>{{ $t('broker.leagues.noData') || 'No Data Available' }}</h3>
                <p>{{ $t('broker.leagues.noDataDesc') || 'This league tier is not available right now.' }}</p>
              </div>

              <!-- Search results empty (but tier has data) -->
              <div v-if="!isLoading && communities.length > 0 && filteredCommunities.length === 0 && searchQuery" class="empty-state">
                <i class="fa-regular fa-search empty-search-icon"></i>
                <h3>{{ $t('broker.leagues.noSearchResults') || 'No Results Found' }}</h3>
                <p>{{ $t('broker.leagues.noSearchResultsDesc') || 'No communities match your search criteria.' }}</p>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LeagueTierTabs from '@/apps/broker/components/Leagues/LeagueTierTabs.vue';
import LeaguePodiumCard from '@/apps/broker/components/Leagues/LeaguePodiumCard.vue';
import LeagueTable from '@/apps/broker/components/Leagues/LeagueTable.vue';
import LeagueShare from '@/apps/broker/components/Leagues/LeagueShare.vue';
import LoadingOverlay from '@/apps/broker/components/LoadingOverlay.vue';
import SkeletonLoader from '@/apps/broker/components/SkeletonLoader.vue';
import leaguesRepository from '@/apps/broker/repositories/leagues.repository';

export default {
  name: 'LeagueRankingView',
  components: {
    LeagueTierTabs,
    LeaguePodiumCard,
    LeagueTable,
    LeagueShare,
    LoadingOverlay,
    SkeletonLoader
  },
  data() {
    return {
      selectedTier: 'bronze',
      searchQuery: '',
      // Loading states
      isLoading: false,
      isInitialLoading: true,
      loadingMessage: 'Loading leagues data...',
      communities: [],
      hasAnyLeagueData: null, // null = not checked yet, true = has data, false = no data
      firstAvailableTier: null, // Store the first tier with data
      showShareDialog: false // Control share dialog visibility
    };
  },
  computed: {
    partnerId() {
      // Get partnerId directly from broker state
      return this.$store.state.broker?.partnerId || 0;
    },
    partnerName() {
      return this.$store.state.partner?.name || this.$store.state.broker?.partner?.name || 'Partner';
    },
    primaryColor() {
      return this.$store.state.partner?.primaryColor || this.$store.state.broker?.partner?.primaryColor || '#28ea96';
    },
    tierApiMapping() {
      return {
        platinum: 'DIAMOND',
        gold: 'GOLD',
        silver: 'SILVER',
        bronze: 'BRONZE'
      };
    },
    currentTierIcon() {
      const icons = {
        platinum: require('@/apps/broker/designs/leagues/platinum-icon.png'),
        gold: require('@/apps/broker/designs/leagues/gold-icon.png'),
        silver: require('@/apps/broker/designs/leagues/silver-icon.png'),
        bronze: require('@/apps/broker/designs/leagues/bronze-icon.png')
      };
      return icons[this.selectedTier];
    },
    currentTierLabel() {
      const labels = {
        platinum: 'Platinum',
        gold: 'Gold',
        silver: 'Silver',
        bronze: 'Bronze'
      };
      return labels[this.selectedTier];
    },
    tierAccentColor() {
      const colors = {
        platinum: '#E5E4E2',
        gold: '#FFD700',
        silver: '#C0C0C0',
        bronze: '#CD7F32'
      };
      return colors[this.selectedTier];
    },
    filteredCommunities() {
      if (!this.searchQuery) {
        return this.communities;
      }
      const query = this.searchQuery.toLowerCase();
      return this.communities.filter(c =>
        c.name.toLowerCase().includes(query)
      );
    },
    top3Communities() {
      // Always show the actual top 3 from API data (not filtered by search)
      return this.communities.slice(0, 3).map((c, index) => ({
        ...c,
        rank: index + 1
      }));
    },
    podiumOrder() {
      // Order: 2nd, 1st, 3rd for visual display
      if (this.top3Communities.length < 2) return this.top3Communities;
      const ordered = [];
      if (this.top3Communities[1]) ordered.push(this.top3Communities[1]); // 2nd
      if (this.top3Communities[0]) ordered.push(this.top3Communities[0]); // 1st
      if (this.top3Communities[2]) ordered.push(this.top3Communities[2]); // 3rd
      return ordered;
    }
  },
  watch: {
    selectedTier() {
      // Only fetch when tier changes after initial load
      if (this.hasAnyLeagueData) {
        this.fetchLeagueData();
      }
    }
  },
  async mounted() {
    await this.checkAllLeagues();
  },
  methods: {
    async checkAllLeagues() {
      // Check all 4 leagues to see if any have data
      const tiers = ['bronze', 'silver', 'gold', 'platinum'];
      const tierMapping = this.tierApiMapping;

      try {
        const checks = await Promise.allSettled(
          tiers.map(tier =>
            leaguesRepository.getLeagueOverview(
              this.partnerId,
              'IBS_LEAGUE',
              tierMapping[tier]
            )
          )
        );

        // Check if any league has data
        let foundDataInTier = null;
        for (let i = 0; i < checks.length; i++) {
          const check = checks[i];
          if (check.status === 'fulfilled' && check.value.clubRanking.length > 0) {
            foundDataInTier = tiers[i];
            break;
          }
        }

        if (foundDataInTier) {
          this.hasAnyLeagueData = true;
          this.firstAvailableTier = foundDataInTier;
          this.selectedTier = foundDataInTier;
          // Fetch data for the first available tier
          await this.fetchLeagueData();
        } else {
          this.hasAnyLeagueData = false;
          this.isInitialLoading = false;
        }
      } catch (error) {
        console.error('Error checking leagues:', error);
        this.hasAnyLeagueData = false;
        this.isInitialLoading = false;
      }
    },
    async fetchLeagueData() {
      // Use overlay loading for subsequent loads, skeleton for initial
      if (this.isInitialLoading) {
        // Keep isInitialLoading true, no need to set isLoading
      } else {
        this.isLoading = true;
      }

      try {
        const leaderboardType = this.tierApiMapping[this.selectedTier];
        const response = await leaguesRepository.getLeagueOverview(
          this.partnerId,
          'IBS_LEAGUE',
          leaderboardType
        );

        // Map API response to component data structure
        this.communities = response.clubRanking.map(club => ({
          id: club.clubId,
          name: club.clubName,
          avatar: club.clubImageUrl,
          volume: club.totalVolume,
          points: null, // Points not available in API response
          rank: club.rank,
          memberCount: club.memberCount
        }));
      } catch (error) {
        console.error('Error fetching league data:', error);

        // Handle 404 or empty responses (league not available)
        if (error.response?.status === 404 || error.response?.status === 204) {
          this.communities = [];
        } else {
          this.$store.commit('showSnackbar', {
            text: this.$t('broker.leagues.error') || 'Failed to load league data',
            color: 'error'
          });
        }
      } finally {
        this.isLoading = false;
        this.isInitialLoading = false;
      }
    },
    openShareDialog() {
      this.showShareDialog = true;
    },
    onLinkCopied() {
      // Show success message when link is copied
      if (this.$store.commit) {
        this.$store.commit('showSnackbar', {
          text: this.$t('broker.leagues.linkCopied') || 'Link copied to clipboard!',
          color: 'success'
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.league-ranking-view {
  padding: 0;
  background: transparent;
}

.page-header {
  margin-bottom: 40px;

  .text-body-1 {
    line-height: 1.6 !important;
    max-width: 800px;
  }
}

.header-content {
  max-width: 100%;
}

.title-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.page-title {
  margin: 0 !important;
  line-height: 1;
}

.tier-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 24px;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(120deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%);
    background-size: 200% 100%;
    animation: shimmer 3s infinite;
  }

  .tier-badge-icon {
    width: 24px;
    height: 24px;
    object-fit: contain;
    position: relative;
    z-index: 1;
  }

  .tier-badge-label {
    position: relative;
    z-index: 1;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  &.tier-platinum {
    background: linear-gradient(135deg, #D4E4F7 0%, #F0F4F8 100%);

    .tier-badge-label {
      color: #2C5F8D !important;
    }
  }

  &.tier-gold {
    background: linear-gradient(135deg, #FFD700 0%, #FFF4CC 100%);

    .tier-badge-label {
      color: #8B6914 !important;
    }
  }

  &.tier-silver {
    background: linear-gradient(135deg, #C0C0C0 0%, #E8E8E8 100%);

    .tier-badge-label {
      color: #4A4A4A !important;
    }
  }

  &.tier-bronze {
    background: linear-gradient(135deg, #CD7F32 0%, #E8B88A 100%);

    .tier-badge-label {
      color: #3D1F0F !important;
    }
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.page-subtitle {
  margin: 0;
}

.search-share-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 24px 0;
  gap: 16px;
}

.search-container {
  flex: 0 0 360px;

  .search-input {
    ::v-deep .v-input__slot {
      border-radius: 8px;
    }
  }
}

.share-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  padding: 0 24px !important;
  height: 40px !important;
}

.tier-content {
  width: 100%;
}

.podium-section {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 32px;
  align-items: end;
}

.table-section {
  margin-bottom: 32px;
}

.general-empty-state {
  .empty-state-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 100px 20px;
    text-align: center;
    min-height: 400px;

    .empty-state-icon {
      display: inline-block;
      width: 120px;
      height: 120px;
      background-color: v-bind(primaryColor);
      mask-image: url('@/apps/broker/designs/leagues/league.svg');
      mask-size: contain;
      mask-repeat: no-repeat;
      mask-position: center;
      -webkit-mask-image: url('@/apps/broker/designs/leagues/league.svg');
      -webkit-mask-size: contain;
      -webkit-mask-repeat: no-repeat;
      -webkit-mask-position: center;
      margin-bottom: 20px;
      opacity: 0.3;
    }

    h3 {
      font-family: 'Avenir', sans-serif;
      font-size: 28px;
      font-weight: 600;
      color: var(--v-mainBlack-base, #1A1A1A);
      margin: 0 0 12px 0;
    }

    p {
      font-family: 'Avenir', sans-serif;
      font-size: 18px;
      color: #666666;
      margin: 0;
      max-width: 600px;
      line-height: 1.6;
    }
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;

  .empty-state-icon {
    display: inline-block;
    width: 120px;
    height: 120px;
    background-color: v-bind(primaryColor);
    mask-image: url('@/apps/broker/designs/leagues/league.svg');
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
    -webkit-mask-image: url('@/apps/broker/designs/leagues/league.svg');
    -webkit-mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
    margin-bottom: 20px;
    opacity: 0.3;
  }

  h3 {
    font-family: 'Avenir', sans-serif;
    font-size: 24px;
    font-weight: 600;
    color: var(--v-mainBlack-base, #1A1A1A);
    margin: 0 0 8px 0;
  }

  p {
    font-family: 'Avenir', sans-serif;
    font-size: 16px;
    color: #666666;
    margin: 0;
    max-width: 500px;
    line-height: 1.6;
  }

  .empty-search-icon {
    font-size: 80px;
    color: #CCCCCC;
    margin-bottom: 20px;
  }
}

// Tier transition animations
.tier-fade-enter-active {
  transition: all 0.4s ease;
}

.tier-fade-leave-active {
  transition: all 0.25s ease;
}

.tier-fade-enter {
  opacity: 0;
  transform: translateY(10px);
}

.tier-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 1200px) {
  .podium-section {
    grid-template-columns: 1fr;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }
}

@media (max-width: 768px) {
  .title-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .tier-badge {
    font-size: 14px;
    padding: 6px 12px;

    .tier-badge-icon {
      width: 20px;
      height: 20px;
    }
  }

  .search-share-bar {
    flex-direction: column;
    align-items: stretch;

    .search-container {
      flex: 1;
    }

    .share-btn {
      width: 100%;
    }
  }
}
</style>
